/** One run of cards dealing in together: `start` is the index of its first
 *  card, `skip` the keys carried over from the previous list (they morph via
 *  their shared <ViewTransition> and must not deal in again). */
export interface StaggerBatch {
  start: number;
  at: number;
  skip?: ReadonlySet<string | number>;
}

/** Stagger-skip key for the end-of-list card, which has no item key of its
 *  own: it joins a batch's `skip` when it was already on screen, so it morphs
 *  to its new slot like a surviving card instead of dealing in again. */
export const END_OF_LIST_KEY = "end-of-list";

/** The longest a batch can still be animating (600ms delay cap + 280ms
 *  deal-in), with room: it also stops off-screen appended cards from
 *  replaying the entrance when the virtualizer mounts them much later. */
const STAGGER_WINDOW_MS = 1500;

/** One parameter, deliberately: this is passed bare to Array.filter, which
 *  would hand an optional `now` the element index. */
export function staggerBatchLive(batch: StaggerBatch): boolean {
  return Date.now() - batch.at < STAGGER_WINDOW_MS;
}

/** The batch a card deals in with, or undefined if it does not deal in.
 *
 *  Newest first, but the `skip` test is INSIDE the predicate rather than
 *  applied to the winner: batch `start` values do not increase over time. Page
 *  two appends `{start: 30}`, then an in-place edit within the same 1500ms
 *  window pushes `{start: 5, skip: <every previous key>}` on top of it —
 *  picking the newest batch that merely starts at or below the index handed
 *  card 40 that newer batch, found its key in `skip`, and stripped the class
 *  off a card that was still dealing in. Removing an in-flight animation
 *  cancels it and snaps the card to opaque, which is precisely what keeping a
 *  LIST of batches (rather than one moving boundary) exists to prevent. Tested
 *  in scripts/vt-stagger.test.mjs, which pins `now`. */
export function staggerBatchFor(
  batches: readonly StaggerBatch[],
  index: number,
  key: string | number,
  now: number = Date.now()
): StaggerBatch | undefined {
  return batches.findLast(
    (batch) =>
      now - batch.at < STAGGER_WINDOW_MS &&
      index >= batch.start &&
      !batch.skip?.has(key)
  );
}
